import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    SelectElement,
    TextFieldElement,
} from 'react-hook-form-mui';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
} from '@mui/material';

import { EditButtons } from '@barboxx/admin-components';
import { INGREDIENT_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Ingredient } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';
import { INGREDIENT_CATEGORIES } from '@barboxx/types';
import { enumToFormOptions } from '@barboxx/utils';

interface Props {
    ingredient: Ingredient;
}

export const IngredientDetailsCard = ({ ingredient }: Props) => {
    const [editable, editableActions] = useBool();
    const notifications = useNotifications();

    const mutateUser = useMutation({
        mutationKey: ['ingredient', ingredient.id],
        mutationFn: (data: Ingredient.UpdateRequest) =>
            Api.Core.Ingredient.putIngredient(ingredient.id, data),
    });

    const formContext = useForm<Ingredient>({
        resolver: yupResolver(INGREDIENT_SCHEMA),
        defaultValues: ingredient,
    });

    const onSubmit = async (values: Ingredient.UpdateRequest) => {
        mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                editableActions.off();
                notifications.show('Updated ingredient!', {
                    severity: 'success',
                });

                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating ingredient!', {
                    severity: 'error',
                });
            });
    };

    const handleCancel = () => {
        editableActions.off();

        formContext.reset();
    };

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Card>
                <CardHeader title="Ingredient Details" />
                <CardContent>
                    <Stack gap={2}>
                        <TextFieldElement
                            fullWidth
                            name="name"
                            label="Name"
                            slotProps={{
                                input: {
                                    readOnly: !editable,
                                },
                            }}
                        />
                        <TextFieldElement
                            fullWidth
                            name="description"
                            label="Description"
                            slotProps={{
                                input: {
                                    readOnly: !editable,
                                },
                            }}
                        />
                        <SelectElement
                            label="Category"
                            name="category"
                            fullWidth
                            slotProps={{
                                input: {
                                    readOnly: !editable,
                                },
                            }}
                            options={enumToFormOptions(INGREDIENT_CATEGORIES)}
                        />
                        <CheckboxElement
                            label="Is Alcoholic"
                            name="isAlcoholic"
                            disabled={!editable}
                        />
                    </Stack>
                </CardContent>
                <CardActions
                    sx={{
                        justifyContent: 'flex-end',
                        paddingRight: 3,
                        paddingBottom: 3,
                    }}
                >
                    <Can I="update" a="Ingredient">
                        <EditButtons
                            formContext={formContext}
                            handleCancel={handleCancel}
                            editable={editable}
                            editableActions={editableActions}
                        />
                    </Can>
                </CardActions>
            </Card>
        </FormContainer>
    );
};
